export class RequestTimeoutError extends Error {
  constructor(message = 'Request timed out') {
    super(message);
    this.name = 'RequestTimeoutError';
  }
}

export const withTimeout = async <T,>(operation: PromiseLike<T>, timeoutMs = 20_000): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      Promise.resolve(operation),
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new RequestTimeoutError()), timeoutMs);
      }),
    ]);
  } finally {
    if (timer) clearTimeout(timer);
  }
};

// A deadline must also cancel the work, otherwise a timed-out request can
// continue uploading or saving after the user has already tried again.
export const withAbortTimeout = async <T,>(
  operation: (signal: AbortSignal) => PromiseLike<T>,
  timeoutMs = 25_000,
  parentSignal?: AbortSignal,
): Promise<T> => {
  const controller = new AbortController();
  let rejectAbort: (reason:unknown)=>void=()=>{};
  const aborted=new Promise<never>((_resolve,reject)=>{rejectAbort=reject;});
  void aborted.catch(()=>{}); // Also handle an already-aborted parent before operation starts.
  const onAbort=()=>rejectAbort(controller.signal.reason??new Error('Request aborted'));
  controller.signal.addEventListener('abort',onAbort,{once:true});
  const abortFromParent = () => controller.abort(parentSignal?.reason);
  parentSignal?.addEventListener('abort', abortFromParent, { once: true });
  try {
    if (parentSignal?.aborted) abortFromParent();
    controller.signal.throwIfAborted();
    return await withTimeout(Promise.race([Promise.resolve(operation(controller.signal)),aborted]), timeoutMs);
  } catch (reason) {
    controller.abort(reason);
    throw reason;
  } finally {
    parentSignal?.removeEventListener('abort', abortFromParent);
    controller.signal.removeEventListener('abort',onAbort);
  }
};

export const fetchWithTimeout: typeof fetch = (input, init) => withAbortTimeout(
  (signal) => fetch(input, { ...init, signal }),
  15_000,
  init?.signal ?? (input instanceof Request ? input.signal : undefined),
);

export const assertOnline = () => {
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    throw new Error('Network offline');
  }
};
